import React from "react";

import EmployerBreadcrumbs from "EmployerDashboard/Components/Breadcrumbs";
import { FORM_ERROR } from "final-form";
import arrayMutators from "final-form-arrays";
import createDecorator from "final-form-focus";
import { Field, Form } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { Link, useHistory, RouteComponentProps } from "react-router-dom";
import { validateRequired } from "Utils/Validations";

import Chrome from "../components/Chrome";
import Layout from "../components/Layout";
import { useGetBenAdminRenewalQuery, useUpdateEmployeeContributionsMutation } from "../redux";
import { CardSection } from "Cobalt/Card";
import { Table, HeaderTR, TH, TR, TD } from "Cobalt/TableV2";
import DashboardHeader from "Common/DashboardHeader";
import Icon from "Common/Icon";
import BodyText from "Theme/BodyText";
import Button, { LinkButton } from "Theme/Button";

const focusOnErrors = createDecorator();

const formInitialValues = ({ benAdminRenewal }) => {
  const { employees, medicalCompanyContributionOptions } = benAdminRenewal;
  const medicalOptionValues = medicalCompanyContributionOptions.map((arr) => arr[0]);
  return {
    employeeContributions: employees
      .filter(({ isEligible }) => isEligible)
      .map(({ renewalPolicyExternalID, name, medicalCompanyContributionID }) => ({
        externalId: renewalPolicyExternalID,
        name,
        medicalCompanyContributionID: medicalOptionValues.find((value) => value === medicalCompanyContributionID) || medicalOptionValues[0],
      })),
  };
};

const parseContibutionIDValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  const intValue = parseInt(value, 10);

  if (Number.isNaN(intValue) || intValue < 0) {
    return null;
  }
  return intValue;
};
const EmployeeContributionsPage: React.FC<RouteComponentProps<{}>> = () => {
  const { isLoading: isLoadingBenAdminRenewal, data: benAdminRenewal } = useGetBenAdminRenewalQuery(null);
  const { medicalPlanIds, medicalCompanyContributionOptions } = benAdminRenewal || {};

  const [updateEmployeeContributions] = useUpdateEmployeeContributionsMutation();

  const history = useHistory();
  const submitHandler = async (values) => {
    const res = await updateEmployeeContributions(values);
    if (res.error) {
      const { errors } = res.error.data;
      const { form_error, ...rest } = errors;
      return {
        ...(form_error ? { [FORM_ERROR]: form_error } : {}),
        ...rest,
      };
    }
    history.push("/benefits-administration/renewal/status");
    return null;
  };

  const title = "Employee Contribution";

  const crumbs = [
    { title: "Renewal", link: "/benefits-administration/renewal" },
    { title, link: "" },
  ];

  return (
    <Chrome title="SimplyInsured - Broker Dashboard">
      <Layout>
        <EmployerBreadcrumbs crumbs={crumbs} px={0} />
        <DashboardHeader title={title} px={0}>
          <BodyText>
            Select the class of each employee to set contribution amounts. To edit contribution amounts{" "}
            <Link to="/benefits-administration/renewal/policy-details">click here</Link>.
          </BodyText>
        </DashboardHeader>
        {isLoadingBenAdminRenewal ? (
          <BodyText>Loading...</BodyText>
        ) : (
          <Form
            onSubmit={submitHandler}
            decorators={[focusOnErrors]}
            initialValues={formInitialValues({ benAdminRenewal })}
            mutators={{
              ...arrayMutators,
            }}
          >
            {({ handleSubmit, submitting, submitError }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <FieldArray name="employeeContributions">
                    {({ fields, meta: _meta }) => (
                      <CardSection px={0}>
                        <Table>
                          <thead>
                            <HeaderTR>
                              <TH>Employee</TH>
                              {medicalCompanyContributionOptions.map((option, index) => (
                                <TH width="50px" key={index}>
                                  {option[1]}
                                </TH>
                              ))}
                            </HeaderTR>
                          </thead>
                          <tbody>
                            {fields.map((name, index) => (
                              <TR key={index} highlightFirstCell={false} withBorder={index !== fields.length - 1}>
                                <TD>{fields.value[index].name}</TD>
                                {medicalCompanyContributionOptions.map((option, optionIndex) => (
                                  <TD key={optionIndex}>
                                    <Field
                                      name={`${name}.medicalCompanyContributionID`}
                                      parse={parseContibutionIDValue}
                                      component="input"
                                      type="radio"
                                      value={option[0]}
                                      validate={medicalPlanIds.length > 0 ? validateRequired : undefined}
                                    >
                                      {({ input }) => {
                                        return (
                                          <input
                                            name={input.name}
                                            className="cursor-pointer w-full"
                                            type="radio"
                                            value={option[0]}
                                            checked={input.checked}
                                            onChange={input.onChange}
                                          />
                                        );
                                      }}
                                    </Field>
                                  </TD>
                                ))}
                              </TR>
                            ))}
                          </tbody>
                        </Table>
                        {/* <BodyText>
                        <strong>Form Key</strong>: {fields.name}
                        <br />
                        <strong>Form Key Value:</strong>:<pre>{JSON.stringify(fields.value, null, 2)}</pre>
                        <strong>Form Key Meta:</strong>:<pre>{JSON.stringify(meta, null, 2)}</pre>
                      </BodyText> */}
                      </CardSection>
                    )}
                  </FieldArray>
                  <CardSection className="flex justify-between items-center" px={0}>
                    <LinkButton component={Link} size="medium" to="/benefits-administration/renewal/employee-eligibility" color="link" pl={0}>
                      <Icon icon="chevron-left" pr={3} />
                      Back
                    </LinkButton>
                    {submitError && <BodyText className="text-error-900">{submitError}</BodyText>}
                    <Button type="submit" size="medium" ml="auto" disabled={submitting}>
                      Next
                    </Button>
                  </CardSection>
                </form>
              );
            }}
          </Form>
        )}
      </Layout>
    </Chrome>
  );
};

export default EmployeeContributionsPage;
